import React, {type CSSProperties, type ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {AnimationLightboxProps} from './animation-lightbox.types';
import styles from './animation-lightbox.module.scss';
import {Lightbox} from '@Components/lightbox/lightbox';
import {AnimationLightboxHeaderContent} from '@Components/ai-chat-window/components/animation-lightbox-header-content/animation-lightbox-header-content';
import {AnimationLightboxThumbnailStrip} from '@Components/ai-chat-window/components/animation-lightbox-thumbnail-strip/animation-lightbox-thumbnail-strip';
import {AnimationTrimControls} from '@Components/ai-chat-window/components/animation-trim-controls/animation-trim-controls';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {trimAiChatAnimation} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {isTouchDevice} from '@Utils/browser.util';
import TooltipTrigger from '@Components/tooltip-trigger';
import {generateThumbnailStripCanvas} from '@Utils/video.util';
import {Swiper} from '@Components/swiper';
import {noop} from '@Utils/general.util';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';

export function AnimationLightbox({
  isOpen,
  onClose,
  animations,
  currentIndex,
  onSelectAnimation,
  onAnimationTrimmed,
  chatId,
  isWriteActionEnabled,
  isCreating,
  onStopGeneration,
  imageWidth,
  imageHeight,
}: AnimationLightboxProps): ReactElement | null {
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const pushedStateRef = useRef(false);
  const closedByPopRef = useRef(false);
  const videoBlobCacheRef = useRef<Map<string, string>>(new Map());
  const thumbnailStripCacheRef = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const [thumbnailStripCanvas, setThumbnailStripCanvas] = useState<HTMLCanvasElement | undefined>(undefined);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const isSmallScreen = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);
  const [isCreatingHovered, setIsCreatingHovered] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [knownVideoAspect, setKnownVideoAspect] = useState<number | null>(imageWidth && imageHeight ? imageWidth / imageHeight : null);
  const [isTrimMode, setIsTrimMode] = useState(false);
  const [trimStartTime, setTrimStartTime] = useState(0);
  const [trimEndTime, setTrimEndTime] = useState(0);
  const trimStartTimeRef = useRef(trimStartTime);
  const trimEndTimeRef = useRef(trimEndTime);
  const isTrimModeRef = useRef(isTrimMode);
  const videoElRef = useRef(videoEl);
  const isPlayingRef = useRef(isPlaying);
  const progressFillRef = useRef<HTMLDivElement>(null);
  const progressHandleRef = useRef<HTMLDivElement>(null);
  trimStartTimeRef.current = trimStartTime;
  trimEndTimeRef.current = trimEndTime;
  isTrimModeRef.current = isTrimMode;
  videoElRef.current = videoEl;
  isPlayingRef.current = isPlaying;

  const totalCount = isCreating ? animations.length + 1 : animations.length;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalCount - 1;
  const isCreatingSlot = Boolean(isCreating) && currentIndex === 0;
  const currentAnimation = isCreatingSlot ? null : animations[isCreating ? currentIndex - 1 : currentIndex];
  const creatingAspectRatio = animations[0]?.width && animations[0]?.height ? animations[0].width / animations[0].height : 9 / 16;

  const handleClose = useCallback((): void => {
    if (pushedStateRef.current && !closedByPopRef.current) {
      window.history.back();
      pushedStateRef.current = false;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!videoEl) {
      return;
    }

    const onTimeUpdate = (): void => {
      setCurrentTime(videoEl.currentTime);
      if (!isTrimModeRef.current) {
        if (videoEl.currentTime < trimStartTimeRef.current) {
          // eslint-disable-next-line react-compiler/react-compiler
          videoEl.currentTime = trimStartTimeRef.current;
        } else if (trimEndTimeRef.current > 0 && videoEl.currentTime >= trimEndTimeRef.current) {
          videoEl.pause();
        }
      }
    };
    let rafId: number;
    const updateProgressBar = (): void => {
      const start = trimStartTimeRef.current;
      const end = trimEndTimeRef.current;
      const trimDur = end - start;
      const elapsed = Math.max(0, Math.min(trimDur, videoEl.currentTime - start));
      const pct = trimDur > 0 ? (elapsed / trimDur) * 100 : 0;
      if (progressFillRef.current) {
        progressFillRef.current.style.width = `${pct}%`;
      }
      if (progressHandleRef.current) {
        progressHandleRef.current.style.setProperty('--progress-pct', String(pct / 100));
      }
      rafId = requestAnimationFrame(updateProgressBar);
    };

    const onPlay = (): void => {
      setIsPlaying(true);
      rafId = requestAnimationFrame(updateProgressBar);
    };
    const onPause = (): void => {
      setIsPlaying(false);
      cancelAnimationFrame(rafId);
      updateProgressBar();
    };
    const onEnded = (): void => {
      setIsPlaying(false);
      cancelAnimationFrame(rafId);
      updateProgressBar();
    };
    const onCanPlay = (): void => {
      setIsVideoLoading(false);
      if (!isTrimModeRef.current && videoEl.currentTime < trimStartTimeRef.current) {
        videoEl.currentTime = trimStartTimeRef.current;
      }
      const url = videoEl.src;
      const animUrl = currentAnimation?.animationUrl;
      if (url && !url.startsWith('blob:') && !videoBlobCacheRef.current.has(url)) {
        fetch(url)
          .then((res) => res.blob())
          .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            videoBlobCacheRef.current.set(url, blobUrl);
            if (animUrl && !thumbnailStripCacheRef.current.has(animUrl) && currentAnimation) {
              const containerWidth = window.innerWidth;
              generateThumbnailStripCanvas(blobUrl, currentAnimation.duration, currentAnimation.width, currentAnimation.height, containerWidth)
                .then((canvas: HTMLCanvasElement) => {
                  thumbnailStripCacheRef.current.set(animUrl, canvas);
                  setThumbnailStripCanvas(canvas);
                })
                .catch(noop);
            }
          })
          .catch((): void => {
            // silently ignore — video already played fine from the original URL
          });
      } else if (animUrl && !thumbnailStripCacheRef.current.has(animUrl) && currentAnimation) {
        const sourceUrl = videoBlobCacheRef.current.get(animUrl) ?? animUrl;
        const containerWidth = window.innerWidth;
        generateThumbnailStripCanvas(sourceUrl, currentAnimation.duration, currentAnimation.width, currentAnimation.height, containerWidth)
          .then((canvas: HTMLCanvasElement) => {
            thumbnailStripCacheRef.current.set(animUrl, canvas);
            setThumbnailStripCanvas(canvas);
          })
          .catch(noop);
      } else if (animUrl && thumbnailStripCacheRef.current.has(animUrl)) {
        setThumbnailStripCanvas(thumbnailStripCacheRef.current.get(animUrl));
      }
    };

    const onLoadedMetadata = (): void => {
      if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        setKnownVideoAspect(videoEl.videoWidth / videoEl.videoHeight);
      }
    };

    videoEl.addEventListener('timeupdate', onTimeUpdate);
    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);
    videoEl.addEventListener('ended', onEnded);
    videoEl.addEventListener('canplay', onCanPlay);
    videoEl.addEventListener('loadedmetadata', onLoadedMetadata);

    return (): void => {
      cancelAnimationFrame(rafId);
      videoEl.removeEventListener('timeupdate', onTimeUpdate);
      videoEl.removeEventListener('play', onPlay);
      videoEl.removeEventListener('pause', onPause);
      videoEl.removeEventListener('ended', onEnded);
      videoEl.removeEventListener('canplay', onCanPlay);
      videoEl.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [videoEl]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const indices = [currentIndex - 1, currentIndex + 1];
    indices.forEach((i) => {
      const anim = animations[isCreating ? i - 1 : i];
      if (!anim) {
        return;
      }
      const url = anim.animationUrl;
      if (videoBlobCacheRef.current.has(url)) {
        return;
      }
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          videoBlobCacheRef.current.set(url, URL.createObjectURL(blob));
        })
        .catch(noop);
    });
  }, [isOpen, currentIndex, animations, isCreating]);

  useEffect(() => {
    if (!isOpen || !videoEl || !currentAnimation) {
      return;
    }
    setCurrentTime(0);
    setIsPlaying(false);
    const originalUrl = currentAnimation.animationUrl;
    const cachedUrl = videoBlobCacheRef.current.get(originalUrl);
    if (cachedUrl) {
      videoEl.src = cachedUrl;
    } else {
      setIsVideoLoading(true);
      videoEl.src = originalUrl;
    }
    videoEl.load();
    void videoEl.play().catch((): void => {
      // autoplay may be blocked by browser policy
    });
  }, [isOpen, currentIndex, currentAnimation, videoEl]);

  useEffect(() => {
    setIsTrimMode(false);
    if (currentAnimation) {
      setTrimStartTime(currentAnimation.startTime ?? 0);
      setTrimEndTime(currentAnimation.endTime ?? currentAnimation.duration);
      const cached = thumbnailStripCacheRef.current.get(currentAnimation.animationUrl);
      setThumbnailStripCanvas(cached);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (!isOpen && videoEl) {
      videoEl.pause();
      videoEl.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
      setIsVideoLoading(false);
    }
  }, [isOpen, videoEl]);

  useEffect(() => {
    const cache = videoBlobCacheRef.current;
    return (): void => {
      cache.forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isOpen) {
        return;
      }
      if (event.key === 'Escape') {
        if (isTrimModeRef.current) {
          setIsTrimMode(false);
        } else {
          handleClose();
        }
      } else if (event.key === ' ' && !isTrimModeRef.current) {
        event.preventDefault();
        const el = videoElRef.current;
        if (el) {
          if (isPlayingRef.current) {
            el.pause();
          } else {
            void el.play().catch(noop);
          }
        }
      } else if (event.key === 'ArrowLeft' && hasPrevious && !isTrimModeRef.current) {
        event.preventDefault();
        onSelectAnimation(currentIndex - 1);
      } else if (event.key === 'ArrowRight' && hasNext && !isTrimModeRef.current) {
        event.preventDefault();
        onSelectAnimation(currentIndex + 1);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, hasPrevious, hasNext, currentIndex, handleClose, onSelectAnimation]);

  useEffect(() => {
    const onPopState = (): void => {
      if (pushedStateRef.current) {
        pushedStateRef.current = false;
        closedByPopRef.current = true;
        onClose();
      }
    };

    if (isOpen && !pushedStateRef.current) {
      closedByPopRef.current = false;
      window.history.pushState({pmwAnimationLightbox: true, unique: Date.now()}, document.title);
      pushedStateRef.current = true;
      window.addEventListener('popstate', onPopState);
    } else if (isOpen && pushedStateRef.current) {
      window.addEventListener('popstate', onPopState);
    }

    return (): void => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isOpen, onClose]);

  if (!currentAnimation && !isCreatingSlot) {
    return null;
  }

  const handleEnterTrimMode = (): void => {
    if (!currentAnimation) {
      return;
    }
    if (videoEl) {
      videoEl.pause();
    }
    setTrimStartTime(currentAnimation.startTime ?? 0);
    setTrimEndTime(currentAnimation.endTime ?? currentAnimation.duration);
    setIsTrimMode(true);
  };

  const handleTrimConfirm = (start: number, end: number): void => {
    setTrimStartTime(start);
    setTrimEndTime(end);
    setIsTrimMode(false);
    if (currentAnimation) {
      onAnimationTrimmed(currentAnimation.animationHashedId, start, end);
      void trimAiChatAnimation(currentAnimation.animationHashedId, start, end, chatId);
    }
  };

  const handleTrimCancel = (): void => {
    setIsTrimMode(false);
  };

  const handleTrimPlay = (): void => {
    if (!videoEl) {
      return;
    }
    void videoEl.play().catch((): void => {
      /* empty */
    });
  };

  const handleTrimPause = (): void => {
    videoEl?.pause();
  };

  const handleTrimSeek = (time: number): void => {
    if (!videoEl) {
      return;
    }
    videoEl.currentTime = time;
  };

  const handlePlayPause = (): void => {
    if (!videoEl) {
      return;
    }
    if (isPlaying) {
      videoEl.pause();
    } else {
      if (trimEndTime > 0 && (videoEl.currentTime < trimStartTime || videoEl.currentTime >= trimEndTime)) {
        videoEl.currentTime = trimStartTime;
      }
      void videoEl.play();
    }
  };

  const handleVolumeToggle = (): void => {
    if (!videoEl) {
      return;
    }
    videoEl.muted = !videoEl.muted;
    setIsMuted(videoEl.muted);
  };

  const handleSeek = (value: number): void => {
    if (!videoEl) {
      return;
    }
    const trimDuration = trimEndTime - trimStartTime;
    if (trimDuration <= 0) {
      return;
    }
    videoEl.currentTime = trimStartTime + (value / 100) * trimDuration;
  };

  const seekFromClientX = (clientX: number, rect: DOMRect): void => {
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    handleSeek(percent);
  };

  const handleSliderMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const wasPlaying = !videoEl?.paused;
    videoEl?.pause();
    seekFromClientX(e.clientX, rect);

    const onMove = (moveE: MouseEvent): void => {
      seekFromClientX(moveE.clientX, rect);
    };
    const onUp = (): void => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (wasPlaying) {
        void videoEl?.play();
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleSliderTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const wasPlaying = !videoEl?.paused;
    videoEl?.pause();
    seekFromClientX(e.touches[0].clientX, rect);

    const onMove = (moveE: TouchEvent): void => {
      seekFromClientX(moveE.touches[0].clientX, rect);
    };
    const onUp = (): void => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      if (wasPlaying) {
        void videoEl?.play();
      }
    };
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onUp);
  };

  const formatTime = (seconds: number): string => {
    const total = Math.round(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const trimDuration = trimEndTime - trimStartTime;
  const elapsedWithinTrim = Math.max(0, Math.min(trimDuration, currentTime - trimStartTime));
  const elapsedPercent = trimDuration > 0 ? (elapsedWithinTrim / trimDuration) * 100 : 0;

  const getStopIconSize = (): IconSize => {
    if (isSmallScreen) {
      return IconSize.SIZE_ICON_12;
    }
    return IconSize.SIZE_ICON_24;
  };

  const getLoaderSize = (): LOADER_SIZE => {
    if (isSmallScreen) {
      return LOADER_SIZE.SMALL;
    }
    return LOADER_SIZE.MEDIUM;
  };

  const getCreatingOverlay = (): ReactElement => {
    if (isCreatingHovered || isTouchDevice()) {
      return (
        <div className={styles.stopOverlay}>
          <ClickableDiv className={styles.stopButtonContainer} onClick={onStopGeneration ?? noop}>
            <CircularProgressLoader color={LOADER_COLOR.DARK} size={getLoaderSize()} classes={styles.stopLoader} />
            <div className={styles.stopIconWrapper}>
              <Icon icon={'icon-square-rounded'} size={getStopIconSize()} className={styles.stopIcon} />
            </div>
          </ClickableDiv>
        </div>
      );
    }
    return (
      <div className={styles.creatingOverlay}>
        <CircularProgressLoader color={LOADER_COLOR.AI_DARK} size={LOADER_SIZE.DEFAULT} />
        <span className={styles.creatingText}>{window.i18next.t('pmwjs_creating_animation_may_take_time')}</span>
      </div>
    );
  };

  const getVideoLoadingOverlay = (): ReactElement | null => {
    if (!isVideoLoading) {
      return null;
    }
    return (
      <div
        className={styles.videoLoadingContainer}
        style={
          currentAnimation?.width && currentAnimation?.height
            ? ({
                '--pmw-aspect': `${currentAnimation.width / currentAnimation.height}`,
                aspectRatio: `${currentAnimation.width} / ${currentAnimation.height}`,
              } as object)
            : undefined
        }
      >
        <SkeletonBrick className={styles.videoLoadingSkeleton} theme={SkeletonBrickTheme.DARK} isPulsating width="100%" height="100%" />
      </div>
    );
  };

  const getVideoContainerContent = (): ReactElement => {
    if (isCreatingSlot) {
      return (
        <div
          className={styles.creatingContainer}
          style={{'--pmw-aspect': String(creatingAspectRatio), aspectRatio: String(creatingAspectRatio)} as object}
          onMouseEnter={(): void => {
            setIsCreatingHovered(true);
          }}
          onMouseLeave={(): void => {
            setIsCreatingHovered(false);
          }}
        >
          <SkeletonBrick className={styles.creatingSkeleton} theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} />
          {getCreatingOverlay()}
        </div>
      );
    }
    return (
      <>
        <video ref={setVideoEl} className={styles.video} playsInline onClick={handlePlayPause}>
          <track kind="captions" />
        </video>
        {getVideoLoadingOverlay()}
      </>
    );
  };

  const getThumbnailStripSection = (): ReactElement | null => {
    if (totalCount <= 1) {
      return null;
    }
    return (
      <div className={`${styles.thumbnailStripWrapper} ${isTrimMode ? styles.thumbnailStripWrapperHidden : ''}`}>
        <AnimationLightboxThumbnailStrip animations={animations} currentIndex={currentIndex} onThumbnailClick={onSelectAnimation} isCreating={isCreating} />
      </div>
    );
  };

  const getMuteButton = (): ReactElement | null => {
    if (!currentAnimation?.hasAudio) {
      return null;
    }
    return (
      <TooltipTrigger text={window.i18next.t(isMuted ? 'pmwjs_unmute' : 'pmwjs_mute')} tooltipClass={styles.controlsTooltip}>
        <ClickableDiv className={styles.frostedButton} onClick={handleVolumeToggle}>
          <Icon icon={isMuted ? 'icon-sound-off' : 'icon-sound'} size={IconSize.SIZE_ICON_20} type={IconType.NONE} shape={IconShape.NONE} />
        </ClickableDiv>
      </TooltipTrigger>
    );
  };

  const getBottomBar = (): ReactElement => {
    if (isTrimMode && currentAnimation) {
      return (
        <AnimationTrimControls
          source={currentAnimation.animationUrl}
          duration={currentAnimation.duration}
          startTime={trimStartTime}
          endTime={trimEndTime}
          videoWidth={currentAnimation.width}
          videoHeight={currentAnimation.height}
          onConfirm={handleTrimConfirm}
          onCancel={handleTrimCancel}
          onPlay={handleTrimPlay}
          onPause={handleTrimPause}
          onSeek={handleTrimSeek}
          videoEl={videoEl}
          preRenderedCanvas={thumbnailStripCanvas}
        />
      );
    }
    return (
      <div className={styles.controlsBar} style={isCreatingSlot ? {visibility: 'hidden', pointerEvents: 'none'} : undefined} aria-hidden={isCreatingSlot ? 'true' : undefined}>
        <Icon
          icon={isPlaying ? 'icon-pause' : 'icon-play-video'}
          size={IconSize.SIZE_ICON_16}
          type={IconType.NONE}
          shape={IconShape.NONE}
          className={styles.playButton}
          onClick={handlePlayPause}
        />
        <span className={styles.timeDisplay}>{`${formatTime(elapsedWithinTrim)} / ${formatTime(trimDuration)}`}</span>
        <div className={styles.sliderContainer} onMouseDown={handleSliderMouseDown} onTouchStart={handleSliderTouchStart}>
          <div className={styles.progressTrack}>
            <div ref={progressFillRef} className={styles.progressFill} style={{width: `${elapsedPercent}%`}} />
            <div ref={progressHandleRef} className={styles.progressHandle} style={{'--progress-pct': elapsedPercent / 100} as CSSProperties} />
          </div>
        </div>
        <div className={styles.controlsSpacer} />
        {getMuteButton()}
        <TooltipTrigger text={window.i18next.t('pmwjs_trim')} tooltipClass={styles.controlsTooltip}>
          <ClickableDiv className={styles.frostedButton} onClick={handleEnterTrimMode}>
            <Icon icon={'icon-cut'} size={IconSize.SIZE_ICON_20} type={IconType.NONE} shape={IconShape.NONE} />
          </ClickableDiv>
        </TooltipTrigger>
      </div>
    );
  };

  return (
    <Lightbox isOpen={isOpen} onClose={handleClose}>
      <div className={styles.container}>
        <AnimationLightboxHeaderContent
          onClose={handleClose}
          chatId={chatId}
          animationHashedId={currentAnimation?.animationHashedId ?? ''}
          isWriteActionEnabled={isWriteActionEnabled && !isCreatingSlot}
          isCreating={isCreatingSlot}
          startTime={currentAnimation?.startTime}
          endTime={currentAnimation?.endTime}
          duration={currentAnimation?.duration}
          isMuted={Boolean(currentAnimation?.hasAudio) && isMuted}
        />
        <div
          className={styles.videoSection}
          onClick={(e): void => {
            if (e.target === e.currentTarget && !isTrimMode) {
              handleClose();
            }
          }}
        >
          <div
            className={styles.videoAndStripGroup}
            onClick={(e): void => {
              if (e.target === e.currentTarget && !isTrimMode) {
                handleClose();
              }
            }}
          >
            <Swiper
              onSwipeLeft={(): void => {
                if (hasNext && !isTrimMode) onSelectAnimation(currentIndex + 1);
              }}
              onSwipeRight={(): void => {
                if (hasPrevious && !isTrimMode) onSelectAnimation(currentIndex - 1);
              }}
              containerClasses={`${styles.videoArea} ${totalCount === 1 ? styles.videoAreaSingle : ''}`}
              onClick={(e): void => {
                if (e.target === e.currentTarget && !isTrimMode) handleClose();
              }}
            >
              <ClickableDiv
                className={`${styles.navArrow} ${!hasPrevious ? styles.navArrowHidden : ''}`}
                onClick={(): void => {
                  if (hasPrevious) {
                    onSelectAnimation(currentIndex - 1);
                  }
                }}
              >
                <Icon icon="icon-caret-left" size={IconSize.SIZE_ICON_24} type={IconType.NONE} shape={IconShape.NONE} className={styles.navArrowIcon} />
              </ClickableDiv>
              <div
                className={`${styles.videoContainer} ${isTrimMode ? styles.videoContainerTrimMode : ''}`}
                style={!isCreatingSlot && knownVideoAspect ? {aspectRatio: String(knownVideoAspect)} : undefined}
                onClick={(e): void => {
                  if (e.target === e.currentTarget && !isTrimMode) {
                    handleClose();
                  }
                }}
              >
                {getVideoContainerContent()}
              </div>
              <ClickableDiv
                className={`${styles.navArrow} ${!hasNext ? styles.navArrowHidden : ''}`}
                onClick={(): void => {
                  if (hasNext) {
                    onSelectAnimation(currentIndex + 1);
                  }
                }}
              >
                <Icon icon="icon-caret-right" size={IconSize.SIZE_ICON_24} type={IconType.NONE} shape={IconShape.NONE} className={styles.navArrowIcon} />
              </ClickableDiv>
            </Swiper>
            {getThumbnailStripSection()}
          </div>
          {getBottomBar()}
        </div>
      </div>
    </Lightbox>
  );
}
